import { Controller } from '@hotwired/stimulus'
import Trix from 'trix'

// Connects to data-controller="trix"
export default class extends Controller {
    static targets = ['editor', 'formattingDropdown', 'fontSizeInput', 'fontSizeUnit', 
                      'foregroundColorPicker', 'backgroundColorPicker']

    connect () {
        this.initTrix()
        this.reloadOriginalContent()
        this.setupClickOutsideHandler()
    }

    disconnect () {
        this.removeClickOutsideHandler()
    }

    initTrix () {
        // Configure heading
        Trix.config.blockAttributes.heading1.tagName = 'h3'

        // Configure alignment block attributes using custom elements
        Trix.config.blockAttributes.alignLeft = {
            tagName: 'text-align-left'
        }
        Trix.config.blockAttributes.alignCenter = {
            tagName: 'text-align-center'
        }
        Trix.config.blockAttributes.alignRight = {
            tagName: 'text-align-right'
        }
        Trix.config.blockAttributes.textJustify = {
            tagName: 'justify-text'
        }

        // Configure font size text attribute
        Trix.config.textAttributes.fontSize = {
            styleProperty: 'font-size',
            inheritable: true
        }

        // Configure color attributes
        Trix.config.textAttributes.foregroundColor = {
            styleProperty: 'color',
            inheritable: true
        }
        Trix.config.textAttributes.backgroundColor = {
            styleProperty: 'backgroundColor',
            inheritable: true
        }

        // Add toolbar buttons
        this.addFormattingButton()
        this.addInsertBIInToolbar()
    }

    reloadOriginalContent () {
        this.editorTarget.reset()
    }

    setupClickOutsideHandler () {
        this.clickOutsideHandler = (event) => {
            if (!this.element.contains(event.target)) {
                this.closeFormattingDropdown()
            }
        }
        document.addEventListener('click', this.clickOutsideHandler)
    }

    removeClickOutsideHandler () {
        document.removeEventListener('click', this.clickOutsideHandler)
    }

    // Formatting dropdown handlers
    toggleFormattingDropdown (event) {
        event.stopPropagation()
        if (this.hasFormattingDropdownTarget) {
            const isVisible = this.formattingDropdownTarget.style.display !== 'none'
            if (isVisible) {
                this.closeFormattingDropdown()
            } else {
                this.openFormattingDropdown()
            }
        }
    }

    openFormattingDropdown () {
        if (this.hasFormattingDropdownTarget) {
            this.formattingDropdownTarget.style.display = 'block'
            this.updateAlignmentButtonStates()
        }
    }

    closeFormattingDropdown () {
        if (this.hasFormattingDropdownTarget) {
            this.formattingDropdownTarget.style.display = 'none'
        }
    }

    // Alignment button handlers
    alignLeft () {
        this.toggleAlignment('alignLeft', ['alignCenter', 'alignRight', 'textJustify'])
    }

    alignCenter () {
        this.toggleAlignment('alignCenter', ['alignLeft', 'alignRight', 'textJustify'])
    }

    alignRight () {
        this.toggleAlignment('alignRight', ['alignLeft', 'alignCenter', 'textJustify'])
    }

    textJustify () {
        this.toggleAlignment('textJustify', ['alignLeft', 'alignCenter', 'alignRight'])
    }

    toggleAlignment (activeAttribute, otherAttributes) {
        const editor = this.editorTarget.editor

        // Deactivate other alignments first
        otherAttributes.forEach(attr => {
            if (editor.attributeIsActive(attr)) {
                editor.deactivateAttribute(attr)
            }
        })

        // Toggle the clicked alignment
        if (editor.attributeIsActive(activeAttribute)) {
            editor.deactivateAttribute(activeAttribute)
        } else {
            editor.activateAttribute(activeAttribute)
        }

        this.updateAlignmentButtonStates()
    }

    updateAlignmentButtonStates () {
        const editor = this.editorTarget.editor
        const alignments = ['alignLeft', 'alignCenter', 'alignRight', 'textJustify']

        alignments.forEach(alignment => {
            const button = this.element.querySelector(`[data-trix-alignment="${alignment}"]`)
            if (button) {
                if (editor.attributeIsActive(alignment)) {
                    button.classList.add('active')
                } else {
                    button.classList.remove('active')
                }
            }
        })
    }

    // Font size handlers
    applyFontSize () {
        const size = this.fontSizeInputTarget.value
        const unit = this.fontSizeUnitTarget.value

        if (size && parseFloat(size) > 0) {
            const fontSize = `${size}${unit}`
            this.editorTarget.editor.activateAttribute('fontSize', fontSize)
        }
    }

    resetFontSize () {
        this.editorTarget.editor.deactivateAttribute('fontSize')
        this.fontSizeInputTarget.value = ''
    }

    // Color handlers
    openForegroundColorPicker () {
        this.foregroundColorPickerTarget.click()
    }

    openBackgroundColorPicker () {
        this.backgroundColorPickerTarget.click()
    }

    foregroundColorChanged () {
        this.editorTarget.editor.activateAttribute('foregroundColor', this.foregroundColorPickerTarget.value)
    }

    backgroundColorChanged () {
        this.editorTarget.editor.activateAttribute('backgroundColor', this.backgroundColorPickerTarget.value)
    }

    addFormattingButton () {
        const toolbar = this.element.querySelector('.trix-button-group.trix-button-group--block-tools')
        if (!toolbar) return

        toolbar.insertAdjacentHTML('beforeend', this.formattingButton)

        // Add dropdown after toolbar
        const toolbarContainer = this.element.querySelector('trix-toolbar')
        if (toolbarContainer) {
            toolbarContainer.style.position = 'relative'
            toolbarContainer.insertAdjacentHTML('afterend', this.formattingDropdown)
        }
    }

    addInsertBIInToolbar () {
        const toolbar = this.element.querySelector('.trix-button-group.trix-button-group--block-tools')
        if (!toolbar) return

        toolbar.insertAdjacentHTML('beforeend', this.insertBI)
    }

    insertBIChat () {
        const attachment = ':chat-dots:'
        this.editorTarget.editor.insertString(attachment)
    }

    get formattingButton () {
        return `
            <button type="button" class="trix-button" data-action="click->trix#toggleFormattingDropdown" title="Formatting options">
                <span class="icon"><i class="bi bi-fonts"></i></span>
            </button>
        `
    }

    get formattingDropdown () {
        return `
            <div class="trix-formatting-dropdown" data-trix-target="formattingDropdown" style="display: none;">
                <div class="trix-formatting-section">
                    <div class="trix-formatting-label">Alignment</div>
                    <div class="trix-formatting-alignment">
                        <button type="button" class="trix-formatting-btn" data-trix-alignment="alignLeft" data-action="click->trix#alignLeft" title="Align left">
                            <i class="bi bi-text-left"></i>
                        </button>
                        <button type="button" class="trix-formatting-btn" data-trix-alignment="alignCenter" data-action="click->trix#alignCenter" title="Align center">
                            <i class="bi bi-text-center"></i>
                        </button>
                        <button type="button" class="trix-formatting-btn" data-trix-alignment="alignRight" data-action="click->trix#alignRight" title="Align right">
                            <i class="bi bi-text-right"></i>
                        </button>
                        <button type="button" class="trix-formatting-btn" data-trix-alignment="textJustify" data-action="click->trix#textJustify" title="Justify">
                            <i class="bi bi-justify"></i>
                        </button>
                    </div>
                </div>
                
                <div class="trix-formatting-section">
                    <div class="trix-formatting-label">Font Size</div>
                    <div class="trix-formatting-fontsize">
                        <input type="number" 
                               min="1" 
                               max="999" 
                               step="0.1" 
                               placeholder="Size" 
                               data-trix-target="fontSizeInput"
                               data-action="input->trix#applyFontSize"
                               title="Font size">
                        <select data-trix-target="fontSizeUnit"
                                data-action="change->trix#applyFontSize"
                                title="Unit">
                            <option value="px" selected>px</option>
                            <option value="em">em</option>
                            <option value="rem">rem</option>
                        </select>
                        <button type="button" class="trix-formatting-btn" data-action="click->trix#resetFontSize" title="Reset font size">
                            <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                    </div>
                </div>
                
                <div class="trix-formatting-section">
                    <div class="trix-formatting-label">Colors</div>
                    <div class="trix-formatting-colors">
                        <input type="color" 
                               style="display: none;"
                               data-trix-target="foregroundColorPicker"
                               data-action="input->trix#foregroundColorChanged">
                        <button type="button" class="trix-formatting-btn" data-action="click->trix#openForegroundColorPicker" title="Text color">
                            <i class="bi bi-palette"></i>
                        </button>
                        
                        <input type="color" 
                               style="display: none;"
                               data-trix-target="backgroundColorPicker"
                               data-action="input->trix#backgroundColorChanged">
                        <button type="button" class="trix-formatting-btn" data-action="click->trix#openBackgroundColorPicker" title="Text background color">
                            <i class="bi bi-paint-bucket"></i>
                        </button>
                    </div>
                </div>
            </div>
        `
    }

    get insertBI () {
        return `<button type="button" class="trix-button" data-action="click->trix#insertBIChat" title="Insert chat icon">
              <span class="icon"><i class="bi bi-chat-dots"></i></span>
            </button>`
    }
}
